import { FC } from "react";
import { SaleTable } from "./components";
import { PageWrapper } from "@connectedComponents";
import { PaymentMethodeEnum, stackEnum } from "@utilities/enums";
import { useControllers } from "./useControllers";
import { Card, Pagination } from "@components";
import { Search, Filter, X, Calendar } from 'lucide-react';

export const SellingManagement: FC = () => {

    const {
        paginatedSales, goBack,
        currentPage,
        totalPages,
        itemsPerPage,
        salesTotals,
        searchTerm,
        showFilters,
        filters,
        activeFiltersCount,
        setShowFilters,
        handleFilterChange,
        clearFilters,
        handleSearchChange,
        handlePageChange,
        handleItemsPerPageChange
    } = useControllers();

    return (
        <PageWrapper
            activeStack={stackEnum.stocksStack}
            handleGoBack={goBack}
        >
            <Card
                title="Gestion des ventes"
            >
                {/* Search Bar & Filters */}
                <div className="mb-4 space-y-3">
                    <div className="flex items-center justify-between">
                        {/* Search Input - Light Mode */}
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Rechercher par N° de commande..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:bg-white transition-all placeholder:text-gray-400"
                            />
                        </div>

                        {/* Filter Toggle Button */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${showFilters || activeFiltersCount > 0
                                ? 'bg-primary text-white border-primary'
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                                }`}
                        >
                            <Filter className="w-4 h-4" />
                            <span>Filtres</span>
                            {activeFiltersCount > 0 && (
                                <span className="bg-white text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                                    {activeFiltersCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="font-medium text-gray-700">Filtres avancés</h4>
                                {activeFiltersCount > 0 && (
                                    <button
                                        onClick={clearFilters}
                                        className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                        Réinitialiser
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                                {/* Payment Method Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-gray-600">Mode de paiement</label>
                                    <select
                                        value={filters.paymentMethod}
                                        onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    >
                                        <option value="">Tous</option>
                                        {Object.entries(PaymentMethodeEnum).map(([key, value]) => (
                                            <option key={key} value={value}>{value}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Date From Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-gray-600">Date début</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="date"
                                            value={filters.dateFrom}
                                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                            className="w-full pl-10 pr-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        />
                                    </div>
                                </div>

                                {/* Date To Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-gray-600">Date fin</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="date"
                                            value={filters.dateTo}
                                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                            className="w-full pl-10 pr-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        />
                                    </div>
                                </div>

                                {/* Min Amount Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-gray-600">Montant min (Fcfa)</label>
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={filters.minAmount}
                                        onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>

                                {/* Max Amount Filter */}
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium text-gray-600">Montant max (Fcfa)</label>
                                    <input
                                        type="number"
                                        placeholder="∞"
                                        value={filters.maxAmount}
                                        onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                                        className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Active Filters Tags */}
                    {activeFiltersCount > 0 && !showFilters && (
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-gray-500">Filtres actifs:</span>
                            {filters.paymentMethod && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-sm rounded-lg">
                                    {filters.paymentMethod}
                                    <button onClick={() => handleFilterChange('paymentMethod', '')} className="hover:text-primary/70">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {filters.dateFrom && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-sm rounded-lg">
                                    Depuis: {new Date(filters.dateFrom).toLocaleDateString('fr-FR')}
                                    <button onClick={() => handleFilterChange('dateFrom', '')} className="hover:text-primary/70">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {filters.dateTo && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-sm rounded-lg">
                                    Jusqu'à: {new Date(filters.dateTo).toLocaleDateString('fr-FR')}
                                    <button onClick={() => handleFilterChange('dateTo', '')} className="hover:text-primary/70">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {filters.minAmount && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-sm rounded-lg">
                                    Min: {parseInt(filters.minAmount).toLocaleString('fr-FR')} Fcfa
                                    <button onClick={() => handleFilterChange('minAmount', '')} className="hover:text-primary/70">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {filters.maxAmount && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-sm rounded-lg">
                                    Max: {parseInt(filters.maxAmount).toLocaleString('fr-FR')} Fcfa
                                    <button onClick={() => handleFilterChange('maxAmount', '')} className="hover:text-primary/70">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Sales Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                        <p className="text-sm text-blue-600 font-medium">Nombre de ventes</p>
                        <p className="text-2xl font-bold text-blue-800">{salesTotals.count}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                        <p className="text-sm text-purple-600 font-medium">Total à payer</p>
                        <p className="text-2xl font-bold text-purple-800">{salesTotals.totalToPay.toLocaleString('fr-FR')} <span className="text-sm font-normal">Fcfa</span></p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                        <p className="text-sm text-green-600 font-medium">Total payé</p>
                        <p className="text-2xl font-bold text-green-800">{salesTotals.totalPaid.toLocaleString('fr-FR')} <span className="text-sm font-normal">Fcfa</span></p>
                    </div>
                    <div className={`bg-gradient-to-br rounded-xl p-4 border ${salesTotals.totalUnpaid > 0
                        ? 'from-orange-50 to-orange-100 border-orange-200'
                        : 'from-gray-50 to-gray-100 border-gray-200'
                        }`}>
                        <p className={`text-sm font-medium ${salesTotals.totalUnpaid > 0 ? 'text-orange-600' : 'text-gray-600'}`}>Reste à payer</p>
                        <p className={`text-2xl font-bold ${salesTotals.totalUnpaid > 0 ? 'text-orange-800' : 'text-gray-800'}`}>
                            {salesTotals.totalUnpaid.toLocaleString('fr-FR')} <span className="text-sm font-normal">Fcfa</span>
                        </p>
                    </div>
                </div>

                <SaleTable
                    paginatedSales={paginatedSales}
                    onReturnClick={() => { }}
                    onDownloadInvoiceClick={() => { }}
                    onPrintClick={() => { }}
                />

                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    showItemsPerPage
                    className='mt-8 justify-between'
                />

            </Card>
        </PageWrapper>
    )
}
